// public/v3/src/components/fax-form-fill-screen.jsx — Form-Fill screen (Phase 4), v2.
//
// TWO-STAGE flow (calm-paper), FRONTEND-only — backend untouched:
//   1. FILL — a clean reconstructed HTML form, GENERATED from the detector's generic
//      field list (any form): native inputs/checkboxes/radios, grouped in reading
//      order, each with a confidence cue + source/coverage + lazy chart suggestions.
//   2. REVIEW — the server-rendered /preview (renderFilledForm) shows the inputs applied
//      to the ORIGINAL received fax — exactly what will be sent — then Confirm & send.
//
// The SERVER stays the source of truth: each field persists via answerField()
// (validated, confirmed:true on 2xx); "Review & sign" flushes any unsaved values;
// completeForm() re-guards. Closing mid-fill is safe + resumable.
//
// Form-agnostic by construction: sections come from field.section (a future detector
// signal) else page; conditional "Please specify"/"Other" fields fall in reading order
// right under the option they follow. No per-form templates.
//
// Props: { groupId, onClose, onDone }.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getForm, answerField, completeForm, getFormPreview, fetchFieldOptions, updatePlacement, stampFields, imageProxyUrl } from '../lib/fax-api';
import { FieldContextRail } from './field-context-rail.jsx';
import { sectionize, optionBoundWriteIns, fieldOptions, writeInInactive, confirmedValueOf, gridParse, gridSerialize } from '../lib/fax-sectionize';

function confirmedCount(data) {
  const a = (data && data.answers) || {};
  return Object.values(a).filter((x) => x && x.confirmed).length;
}
// Mirrors the server's effectivePlacementConfidence: UNGRADED → 'low' (fail
// closed — an ungraded mark's position was never verified by the alignment
// engine, so it needs the same one-tap position check as any low placement).
function fieldPlacementTier(field, answer) {
  if (!field) return 'high';
  if (field.fieldType === 'radio') {
    const opt = (field.options || []).find((o) => String(o.code) === String(answer && answer.value));
    return (opt && opt.placementConfidence) || 'low';
  }
  return field.placementConfidence || 'low';
}
function pendingPositions(data) {
  const fields = (data && data.fields) || [];
  const answers = (data && data.answers) || {};
  const byId = new Map(fields.map((f) => [f.id, f]));
  const confirmedVal = confirmedValueOf(answers);
  return fields.filter((f) => {
    const a = answers[f.id];
    if (!a || !a.confirmed || a.positionConfirmed) return false;
    // A suppressed write-in draws no mark (server lowPlacements skips it too) — its low placement
    // must not hold the send button hostage over ink that will never touch the page.
    if (writeInInactive(f, byId, confirmedVal)) return false;
    if (f.fieldType === 'checkbox') { const v = String(a.value).trim().toLowerCase(); if (v === 'false' || v === '0' || v === 'n' || v === '') return false; }
    return fieldPlacementTier(f, a) === 'low';
  });
}
// Mirrors the server's lowSignaturePlacements: UNGRADED → 'low' (fail closed —
// an ungraded sig/date box was never verified by the aligner, e.g. a per-page
// Textract failure left the raw vision boxes ungraded).
function pendingSignaturePlacements(data, { applySig, stampDate, signerOk }) {
  const sp = (data && data.signaturePlacement) || [];
  const answers = (data && data.answers) || {};
  const out = [];
  for (const s of sp) {
    if (!s) continue;
    // A stamp-queue SKIPPED sig/date draws no ink on that page — nothing to gate
    // (mirrors the server's lowSignaturePlacements).
    if (applySig && signerOk && s.signatureBox && (s.signatureConfidence || 'low') === 'low') { const id = 'sig:' + s.page; if (!(answers[id] && (answers[id].positionConfirmed || answers[id].skipped))) out.push({ id, label: `Signature (page ${s.page})` }); }
    if (stampDate && s.dateBox && !s.dateOccupied && (s.dateConfidence || 'low') === 'low') { const id = 'date:' + s.page; if (!(answers[id] && (answers[id].positionConfirmed || answers[id].skipped))) out.push({ id, label: `Date (page ${s.page})` }); }
  }
  return out;
}

// ── Stamp queue ─────────────────────────────────────────────────────────────
// Build the per-mark decision items for the queue phase: everything that could
// put ink on the page, as ONE-TAP Stamp/Skip proposals in reading order.
// Buckets:
//   ready  — has a proposed value AND a non-low placement → eligible for the
//            "Stamp all ready" bulk tap.
//   eye    — has a value but was DEMOTED (low placement / uncertain detection):
//            the demotion queue proper; individual Stamp/Skip with a ghost crop.
//   choice — no value but the options can BE the buttons (tap = stamp that option).
//   blank  — free-text with no proposal: Skip is primary (type is the exception).
//   done   — already stamped (confirmed) or skipped.
// `inactive` option-bound write-ins (their parent picks a different option) are
// excluded — they will not print regardless.
function buildQueue(data, { stampDate, isInactive }) {
  const fields = (data && data.fields) || [];
  const answers = (data && data.answers) || {};
  const items = [];
  for (const f of fields) {
    if (!f) continue;
    if (isInactive && isInactive(f)) continue;
    const a = answers[f.id] || {};
    const value = a.value != null ? String(a.value) : '';
    const hasVal = value.trim() !== '';
    const uncertain = !!(f.uncertain || a.uncertain);
    const tier = fieldPlacementTier(f, a);
    const opts = fieldOptions(f);
    const isTickable = f.fieldType === 'radio' || f.fieldType === 'choice' || f.fieldType === 'boolean_ynd';
    let bucket;
    if (a.confirmed || a.skipped) bucket = 'done';
    else if (hasVal && !uncertain && tier !== 'low') bucket = 'ready';
    else if (hasVal) bucket = 'eye';
    else if (isTickable && opts.length > 0 && opts.length <= 6) bucket = 'choice';
    else bucket = 'blank';
    // Ghost geometry: the box the ink would land in (selected option box for radios).
    let box = f.answerBox;
    if (f.fieldType === 'radio' && hasVal) { const o = (f.options || []).find((x) => String(x.code) === String(value)); box = (o && o.box) || box; }
    items.push({ id: f.id, kind: 'field', field: f, answer: a, label: f.label, page: Number(f.page) || 1,
      value, hasVal, uncertain, tier, bucket, box, options: opts,
      mark: (f.fieldType === 'checkbox' || f.fieldType === 'radio') ? 'tick' : 'text' });
  }
  for (const s of (data && data.signaturePlacement) || []) {
    if (!s) continue;
    if (s.signatureBox) {
      const id = 'sig:' + s.page;
      const a = answers[id] || {};
      items.push({ id, kind: 'sig', label: `Signature (page ${s.page})`, page: Number(s.page) || 1,
        value: '', hasVal: true, uncertain: false, tier: s.signatureConfidence || 'low',
        bucket: (a.positionConfirmed || a.skipped) ? 'done' : ((s.signatureConfidence || 'low') === 'low' ? 'eye' : 'ready'),
        box: s.signatureBox, options: [], mark: 'sig', answer: a });
    }
    if (s.dateBox && !s.dateOccupied && stampDate) {
      const id = 'date:' + s.page;
      const a = answers[id] || {};
      items.push({ id, kind: 'date', label: `Today's date (page ${s.page})`, page: Number(s.page) || 1,
        value: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
        hasVal: true, uncertain: false, tier: s.dateConfidence || 'low',
        bucket: (a.positionConfirmed || a.skipped) ? 'done' : ((s.dateConfidence || 'low') === 'low' ? 'eye' : 'ready'),
        box: s.dateBox, options: [], mark: 'text', answer: a });
    }
  }
  items.sort((x, y) => (x.page - y.page) || (((x.box && x.box.y) || 0) - ((y.box && y.box.y) || 0)));
  return items;
}

// A cropped strip of a fax page with ghost-ink overlays. Children are positioned
// in FULL-PAGE normalized coords; the wrapper clips to the [y0,y1] band. Client-
// side approximation only — the pixel-true server /preview remains the final gate.
function PageCrop({ src, y0, y1, overlays }) {
  const [imgH, setImgH] = useState(0);
  const wrapH = imgH ? Math.max(48, (y1 - y0) * imgH) : 96;
  return (
    <div style={{ position: 'relative', overflow: 'hidden', height: wrapH, borderRadius: 6, border: '1px solid var(--line, #e5e0d8)', background: '#fff' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: -(y0 * imgH) }}>
        <img src={src} alt="" style={{ width: '100%', display: 'block' }} draggable={false}
          onLoad={(e) => setImgH(e.target.clientHeight)} />
        {imgH > 0 && overlays(imgH)}
      </div>
    </div>
  );
}

// Resolve the page + current box for a repositionable target (a field id, or a
// 'sig:<page>' / 'date:<page>' pseudo-id). Returns { id, page, box } or null when
// there's nothing to move. The box is normalized {x,y,w,h}, origin top-left.
function repositionTarget(data, id) {
  const sig = /^(sig|date):(\d+)$/.exec(String(id || ''));
  if (sig) {
    const page = Number(sig[2]);
    const sp = ((data && data.signaturePlacement) || []).find((s) => s && Number(s.page) === page);
    if (!sp) return null;
    const box = sig[1] === 'date' ? sp.dateBox : sp.signatureBox;
    return box ? { id, page, box } : null;
  }
  const f = ((data && data.fields) || []).find((x) => x && x.id === id);
  if (!f) return null;
  const a = (data && data.answers && data.answers[id]) || {};
  let box = f.answerBox;
  if (f.fieldType === 'radio') { const opt = (f.options || []).find((o) => String(o.code) === String(a.value)); box = (opt && opt.box) || box; }
  // An UNRENDERED mark has no box at all — open the overlay anyway with a neutral mid-page marker;
  // the click-to-place backend synthesizes a real box at the chosen spot.
  if (!box) box = { x: 0.45, y: 0.45, w: f.fieldType === 'checkbox' || f.fieldType === 'radio' ? 0.016 : 0.22, h: f.fieldType === 'checkbox' || f.fieldType === 'radio' ? 0.012 : 0.018 };
  return { id, page: Number(f.page) || 1, box };
}

// Confidence cue tier for a field (VALUE confidence — placement is trusted, verified at review).
function tierOf(answer) {
  if (answer && answer.confirmed) return 'done';
  const v = answer && answer.value;
  if (v == null || String(v).trim() === '') return 'blank';
  const c = (answer && answer.confidence) || 'med';
  return c === 'high' ? 'hi' : c === 'low' ? 'lo' : 'md';
}
const DOT = { done: 'var(--hi)', hi: 'var(--hi)', md: 'var(--md)', lo: 'var(--lo)', blank: 'var(--pink-disabled)' };
// Collapsed-row coverage pill — a GLANCE (the full named reason lives in the rail's
// "Why" card via coverageFlagText). `text` receives the coverage object so it can
// name the NCD (incl. its description) and the reason grounding was skipped.
const COV = {
  covered:     { cls: 'ok',   text: (c) => 'Covered' + (c.citation && c.citation.ncd ? ` · NCD ${c.citation.ncd}${c.citation.ncd_desc ? ' ' + c.citation.ncd_desc : ''} (CMS)` : '') },
  no_med_nec:  { cls: 'warn', text: () => 'May not support medical necessity — choose a covered dx' },
  denied:      { cls: 'warn', text: () => 'Not supportive for this test — choose a covered dx' },
  not_listed:  { cls: 'warn', text: () => 'Not on the CMS national covered list — verify' },
  // not_checked = grounding didn't run (non-Medicare payer / unknown test), NOT
  // "checked and found nothing". Name the reason so it can't read as a denial.
  not_checked: { cls: 'none', text: (c) => 'Coverage not checked' + (c && c.reason ? ` — ${c.reason}` : '') },
};

export function FaxFormFillScreen({ groupId, onClose, onDone }) {
  const [phase, setPhase] = useState('loading'); // loading | queue | fill | review | done | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [values, setValues] = useState({});       // id -> editing value (string / 'true'|'false' / code)
  const [fieldErr, setFieldErr] = useState({});    // id -> inline validation error
  const [openDS, setOpenDS] = useState({});        // id -> decision-support expanded
  const [dsCache, setDsCache] = useState({});      // id -> fetchFieldOptions result (lazy)
  const [applySig, setApplySig] = useState(true);
  const [stampDate, setStampDate] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewUrlRef = useRef(null);
  const [reposition, setReposition] = useState(null); // { id, label, page, box } | null — click-to-place overlay
  const [placePt, setPlacePt] = useState(null);       // { x, y } normalized chosen spot | null
  const [touchUp, setTouchUp] = useState(false);       // review mode: PDF preview ⇄ tap-to-fix pages
  const [fixSel, setFixSel] = useState(null);          // { id, label } — mark being re-placed in touch-up
  const autoOpenedDsRef = useRef(false);              // Phase 2: auto-open DS for unconfirmed dx fields — once per load

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, busy]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getForm(groupId);
        if (cancelled) return;
        setData(d || {});
        const seed = {};
        for (const f of (d && d.fields) || []) { const a = (d.answers || {})[f.id]; seed[f.id] = a && a.value != null ? String(a.value) : ''; }
        setValues(seed);
        // Land on the STAMP QUEUE (one-tap Stamp/Skip per proposed mark) whenever
        // there is anything to decide; the classic form editor stays reachable
        // via "Edit as form". No fields at all → the noFields message (as before).
        setPhase(((d && d.fields) || []).length ? 'queue' : 'fill');
      } catch (e) { if (!cancelled) { setError(e?.message || 'Could not load this form.'); setPhase('error'); } }
    })();
    return () => { cancelled = true; };
  }, [groupId]);

  // Phase 2: auto-open decision support for the dx (icd10) fields a clinician must
  // attest — so the patient's covered diagnoses + coverage verdict are one glance away
  // exactly where a required dx would otherwise be a collapsed dead-end. Runs ONCE when
  // the form first loads (ref-guarded); lazy — the rest stay collapsed + manually
  // expandable. Scope = unconfirmed icd10 fields only.
  //
  // Q2 policy = ADAPTIVE: when there are FEW dx fields (the common deficiency notice),
  // open them all — full discoverability. On a dx-heavy form, open only the FIRST per
  // section (using the SAME sectionize() the form renders with), so a long panel never
  // auto-opens a wall of rails / a burst of field-options fetches.
  useEffect(() => {
    if (phase !== 'fill' || !data || autoOpenedDsRef.current) return;
    autoOpenedDsRef.current = true;
    const allFields = (data.fields) || [];
    const ans = data.answers || {};
    const openable = (f) => f && f.fieldType === 'icd10' && !((ans[f.id]) || {}).confirmed;
    const candidates = allFields.filter(openable);
    if (!candidates.length) return;
    const FEW = 4;
    const toOpen = candidates.length <= FEW
      ? candidates
      : sectionize(allFields).map((g) => (g.fields || []).find(openable)).filter(Boolean);
    if (!toOpen.length) return;
    setOpenDS((m) => { const n = { ...m }; for (const f of toOpen) n[f.id] = true; return n; });
    for (const f of toOpen) loadDS(f.id);
  }, [phase, data]);

  const fields = (data && data.fields) || [];
  const answers = (data && data.answers) || {};
  const noFields = phase !== 'loading' && phase !== 'error' && fields.length === 0;
  const fieldsById = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);
  const hasValue = useCallback((id) => String(values[id] != null ? values[id] : '').trim() !== '', [values]);
  // valueOf for the write-in gate: the value the parent will hold CONFIRMED after the Review flush —
  // the client-side prediction of the server's confirmed-only conditionalSuppressed, so the row only
  // dims when the value truly won't print. An in-progress edit wins (radio toggles react instantly,
  // no round-trip); a saved confirmed answer counts; a seeded-but-unconfirmed value counts ONLY if
  // the flush will auto-confirm it — which it won't for an untouched UNCERTAIN parent (the user must
  // judge those), exactly the case where the server fails open and still requires the write-in, so
  // dimming it would wedge the form behind a disabled input.
  const confirmingValue = useCallback((id) => {
    const f = fieldsById.get(id);
    const a = answers[id] || {};
    const v = values[id] != null ? values[id] : '';
    if (String(a.value == null ? '' : a.value) !== String(v)) return v; // active edit: commit in flight / will flush
    if (a.confirmed === true) return a.value;
    if ((f && f.uncertain) || a.uncertain) return null;                 // untouched uncertain: flush skips it
    return hasValue(id) ? v : null;                                     // seeded value the flush WILL confirm
  }, [fieldsById, values, answers, hasValue]);

  // Persist one field (validated server-side). Returns true on success.
  const commit = useCallback(async (id, value) => {
    try {
      const r = await answerField(groupId, id, value);
      setData((prev) => ({ ...prev, answers: { ...((prev && prev.answers) || {}), ...((r && r.answers) || {}) } }));
      setFieldErr((e) => { const n = { ...e }; delete n[id]; return n; });
      return true;
    } catch (e) {
      setFieldErr((er) => ({ ...er, [id]: e?.message || 'That value was rejected.' }));
      return false;
    }
  }, [groupId]);

  // Lazy decision-support fetch for a field (chart options / search), on expand.
  const loadDS = useCallback(async (id) => {
    if (dsCache[id]) return;
    try { const c = await fetchFieldOptions(groupId, id, ''); setDsCache((m) => ({ ...m, [id]: c || { options: [] } })); }
    catch (_) { setDsCache((m) => ({ ...m, [id]: { options: [] } })); }
  }, [groupId, dsCache]);

  const fixPlacement = useCallback(async (fieldId, action) => {
    if (busy) return;
    setBusy(true); setError(null);
    try { await updatePlacement(groupId, fieldId, action); const d = await getForm(groupId); setData(d || {}); }
    catch (e) { setError(e?.message || 'Could not update the position.'); }
    finally { setBusy(false); }
  }, [groupId, busy]);

  // Open the click-to-place overlay for a flagged item (field or sig/date pseudo-id).
  const startReposition = useCallback((id, label) => {
    const t = repositionTarget(data, id);
    if (!t) { setError('No mark to move for this item.'); return; }
    setPlacePt(null);
    setReposition({ ...t, label });
  }, [data]);

  // Entering touch-up placing from the position panel: bring the mark's page into view so the
  // "tap the exact spot" step starts looking at the right part of the form.
  useEffect(() => {
    if (!fixSel || !touchUp) return;
    const el = document.querySelector(`.ffx-touch-page[data-page="${fixSel.page || 1}"]`) || document.querySelector('.ffx-touch');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [fixSel, touchUp]);

  // Touch-up placement: the reviewer tapped a mark, then tapped where it belongs on the page.
  // Same `place` backend as the reposition overlay; the preview re-renders via its data dependency.
  const fixHere = useCallback(async (x, y) => {
    if (!fixSel || busy) return;
    setBusy(true); setError(null);
    try {
      await updatePlacement(groupId, fixSel.id, { type: 'place', x, y });
      const d = await getForm(groupId); setData(d || {});
      setFixSel(null);
    } catch (e) { setError(e?.message || 'Could not move the mark.'); }
    finally { setBusy(false); }
  }, [groupId, fixSel, busy]);

  // Commit the chosen spot as an absolute placement, then reload + close.
  const placeHere = useCallback(async () => {
    if (!reposition || !placePt || busy) return;
    setBusy(true); setError(null);
    try {
      await updatePlacement(groupId, reposition.id, { type: 'place', x: placePt.x, y: placePt.y });
      const d = await getForm(groupId); setData(d || {});
      setReposition(null); setPlacePt(null);
    } catch (e) { setError(e?.message || 'Could not move the mark.'); }
    finally { setBusy(false); }
  }, [groupId, reposition, placePt, busy]);

  // Stamp-queue decisions: one POST per tap (or one for the bulk header action).
  // items = [{ id, action:'stamp'|'skip', value? }]; the server confirms value +
  // position atomically and returns only the changed entries (spread-merge).
  const decide = useCallback(async (items) => {
    if (busy || !items.length) return;
    setBusy(true); setError(null);
    try {
      const r = await stampFields(groupId, items);
      setData((prev) => ({ ...prev, answers: { ...((prev && prev.answers) || {}), ...((r && r.answers) || {}) } }));
      // Keep the fill editor's local values in sync with any option-chip stamps.
      setValues((m) => {
        const n = { ...m };
        for (const it of items) if (it.action === 'stamp' && it.value != null && !/^(sig|date):/.test(it.id)) n[it.id] = String(it.value);
        return n;
      });
    } catch (e) { setError(e?.message || 'Could not apply that decision.'); }
    finally { setBusy(false); }
  }, [groupId, busy]);

  // "Review & sign": flush every field whose current value isn't already saved as such,
  // then move to the server preview. Any rejected value blocks + surfaces inline.
  // Flushes run CONCURRENTLY (small pool) — the old one-awaited-round-trip-per-field
  // loop made a 15-field form pay 15 serial RTTs before Review. Safe now that the
  // server persists each answer with a per-key atomic jsonb_set (concurrent saves for
  // different fields compose) and returns only the changed entry (spread-merges can't
  // clobber a sibling). commit() already handles per-field errors/fieldErr itself.
  const toReview = useCallback(async () => {
    setBusy(true); setError(null);
    const toFlush = [];
    for (const f of fields) {
      const v = values[f.id] != null ? values[f.id] : '';
      const a = answers[f.id] || {};
      const changed = String(a.value == null ? '' : a.value) !== String(v);
      // Never AUTO-confirm an untouched uncertain field — the user must judge it real (edit/confirm it)
      // or leave it (it won't be stamped and won't block sending). Only commit if they changed it.
      if ((f.uncertain || a.uncertain) && !a.confirmed && !changed) continue;
      // An INACTIVE option-bound write-in (its parent question will hold a different option) is not
      // flushed: the server suppresses it at render anyway, and confirming it would misstate intent.
      if (writeInInactive(f, fieldsById, confirmingValue)) continue;
      if ((!a.confirmed || changed) && hasValue(f.id)) toFlush.push({ id: f.id, v });
    }
    let ok = true;
    const failedIds = [];
    const POOL = 4;
    let cursor = 0;
    const worker = async () => {
      while (cursor < toFlush.length) {
        const item = toFlush[cursor++];
        const good = await commit(item.id, item.v);
        if (!good) { ok = false; failedIds.push(item.id); }
      }
    };
    await Promise.all(Array.from({ length: Math.min(POOL, toFlush.length) }, worker));
    setBusy(false);
    if (ok) { setError(null); setPhase('review'); }
    else {
      setError('Some values were rejected — fix the highlighted fields, then Review again.');
      // Take the reviewer TO the first rejected field (rendered order), instead of
      // leaving them to hunt for the highlight on a long form.
      const rows = Array.from(document.querySelectorAll('.ffx .ffx-row[data-fid]'));
      const first = rows.find((r) => failedIds.includes(r.getAttribute('data-fid')));
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [fields, values, answers, commit, fieldsById, confirmingValue, hasValue]);

  const complete = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      // A SKIPPED field is an explicit decision (renders blank by choice) — it
      // counts as decided, mirroring the server's isComplete.
      const nDone = fields.filter((f) => answers[f.id] && (answers[f.id].confirmed || answers[f.id].skipped)).length;
      const r = await completeForm(groupId, { allowIncomplete: nDone < fields.length, skipSignature: !applySig, skipDate: !stampDate });
      if (r && r.ok) { setPhase('done'); onDone?.(); } else setError((r && r.error) || 'The form did not complete.');
    } catch (e) { setError(e?.message || 'Could not complete the form.'); }
    finally { setBusy(false); }
  }, [groupId, onDone, fields, answers, applySig, stampDate]);

  // Review preview (server render onto the original).
  useEffect(() => {
    if (phase !== 'review') return;
    let cancelled = false;
    setPreviewLoading(true); setPreviewError(null);
    (async () => {
      try {
        const r = await getFormPreview(groupId, { skipSignature: !applySig, skipDate: !stampDate });
        if (cancelled) return;
        if (r && r.error) { setPreview({ url: null, returnFax: r.returnFax || null, signerOk: !!r.signerOk, unrendered: [] }); setPreviewError(r.error); return; }
        let url = null;
        if (r && r.pdfBase64) { try { url = URL.createObjectURL(new Blob([Uint8Array.from(atob(r.pdfBase64), (c) => c.charCodeAt(0))], { type: 'application/pdf' })); } catch (_) { url = null; } }
        if (cancelled) { if (url) URL.revokeObjectURL(url); return; }
        if (previewUrlRef.current && previewUrlRef.current !== url) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreview({ url, returnFax: (r && r.returnFax) || null, signerOk: !!(r && r.signerOk), unrendered: (r && r.unrendered) || [], healed: (r && r.healed) || [], marks: (r && r.marks) || [], qa: (r && r.qa) || null });
      } catch (e) { if (!cancelled) { setPreviewError(e?.message || 'Could not render the preview.'); setPreview(null); } }
      finally { if (!cancelled) setPreviewLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [phase, groupId, applySig, stampDate, data]);
  useEffect(() => () => { if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; } }, []);

  const nConfirmed = confirmedCount(data);
  // Same predicate the rows' data-need uses (incl. the inactive-write-in exclusion),
  // so the "N to review" jump button always has exactly N targets.
  const reviewN = fields.filter((f) => {
    const t = tierOf(answers[f.id]);
    if (t !== 'md' && t !== 'lo') return false;
    return !writeInInactive(f, fieldsById, confirmingValue);
  }).length;
  // Prefill-coverage orientation for the header: how much the chart/AI already did,
  // so a 30-field form reads as "check these few", not "fill all of this".
  const prefilledN = fields.filter((f) => {
    const a = answers[f.id];
    return a && a.value != null && String(a.value).trim() !== '' && a.source && a.source !== 'manual' && a.source !== 'none';
  }).length;

  // Footer "N to check" → JUMP to the next field needing attention, cycling in
  // rendered (sectionized) order. DOM query, not the fields array: sections reorder
  // rows, and the DOM is the order the reviewer actually scrolls.
  const jumpIdxRef = useRef(-1);
  const jumpToNextReview = useCallback(() => {
    const rows = Array.from(document.querySelectorAll('.ffx .ffx-row[data-need]'));
    if (!rows.length) return;
    jumpIdxRef.current = (jumpIdxRef.current + 1) % rows.length;
    const row = rows[jumpIdxRef.current];
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = row.querySelector('input, textarea, select');
    if (input && input.focus) input.focus({ preventScroll: true });
  }, []);

  const positionsPending = pendingPositions(data).length + pendingSignaturePlacements(data, { applySig, stampDate, signerOk: !!(preview && preview.signerOk) }).length;
  const headTitle = (data && data.subtypeLabel) || 'Fill form';

  // ── one field control ──────────────────────────────────────────────────
  function control(f) {
    const a = answers[f.id] || {};
    const v = values[f.id] != null ? values[f.id] : '';
    const tier = tierOf(a);
    const tcls = tier === 'lo' ? ' lo' : tier === 'md' ? ' md' : '';
    const set = (val) => setValues((m) => ({ ...m, [f.id]: val }));

    if (f.fieldType === 'checkbox') {
      const checked = v === 'true' || v === true;
      return <>
        <label className="ffx-check"><input type="checkbox" name={'ffx_' + f.id} checked={checked} disabled={busy}
          onChange={(e) => { const nv = e.target.checked ? 'true' : 'false'; set(nv); commit(f.id, nv); }} /> {checked ? 'Checked' : 'Mark this box'}</label>
        {checked && !f.answerBox && <div className="ffx-src" style={{ display: 'block', marginTop: 4 }}>The exact box for this mark couldn't be measured — you'll click its spot on the page at Review.</div>}
      </>;
    }
    if (f.fieldType === 'radio' || f.fieldType === 'choice' || f.fieldType === 'boolean_ynd') {
      const opts = fieldOptions(f); // radio/choice options, or synthesized Yes/No/Declined for boolean_ynd
      if (!opts.length) return <input className={'ffx-in' + tcls} name={'ffx_' + f.id} value={v} disabled={busy} onChange={(e) => set(e.target.value)} onBlur={() => commit(f.id, v)} />;
      // Write-ins the association pass bound to a SPECIFIC option render nested directly under it (e.g.
      // "There is a medically necessary justification … → Please specify: ___"), so the connection is
      // visible. They're excluded from the top-level flow by displayOrder, and stay their own fields.
      const bound = optionBoundWriteIns(fields, f.id);
      const selOpt = opts.find((o) => String(v) === String(o.code));
      return <div className="ffx-radio" role="group" aria-label={f.label}>
        {selOpt && !selOpt.box && <div className="ffx-src" style={{ display: 'block', marginBottom: 4 }}>The exact box for this choice couldn't be measured — you'll click its spot on the page at Review.</div>}
        {opts.map((o, i) => (
          <React.Fragment key={o.code != null ? o.code : i}>
            <label className={'ffx-opt' + (String(v) === String(o.code) ? ' sel' : '')}>
              <input type="radio" name={'ffx_' + f.id} checked={String(v) === String(o.code)} disabled={busy}
                onChange={() => { set(String(o.code)); commit(f.id, o.code); }} />
              <span className="ot">{o.label || String(o.code)}</span>
            </label>
            {(bound[String(o.code)] || []).map((w) => (
              <div key={w.id} className="ffx-opt-writein" style={{ margin: '2px 0 8px 26px', paddingLeft: 10, borderLeft: '2px solid var(--line, #e5e0d8)' }}>
                {fieldRow(w)}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>;
    }
    // ENTRY GRID (the form prints repeated "____ Date(s): ___" rows): edit as structured rows —
    // each lands on its own printed line with its date in the Date(s) cell. The payer gets dates.
    if (Array.isArray(f.gridRows) && f.gridRows.length > 1) {
      const maxRows = f.gridRows.length;
      const entries = gridParse(v, maxRows);
      const shown = Math.min(maxRows, Math.max(entries.length + (entries[entries.length - 1] && (entries[entries.length - 1].text || entries[entries.length - 1].date) ? 1 : 0), 2));
      const update = (i, key, val) => {
        const next = [];
        for (let j = 0; j < Math.max(shown, entries.length, i + 1); j++) next.push({ ...(entries[j] || { text: '', date: '' }) });
        next[i] = { ...next[i], [key]: val };
        set(gridSerialize(next));
      };
      const flushGrid = () => { const nv = values[f.id] != null ? values[f.id] : ''; if (String(nv).trim() !== '') commit(f.id, nv); };
      const clearRow = (i) => {
        const next = [];
        for (let j = 0; j < Math.max(shown, entries.length); j++) next.push({ ...(entries[j] || { text: '', date: '' }) });
        next[i] = { text: '', date: '' };
        const nv = gridSerialize(next);
        set(nv);
        if (nv) commit(f.id, nv);
      };
      return <div className="ffx-grid" role="group" aria-label={f.label}>
        <div className="ffx-grid-head" aria-hidden="true">
          <span className="ffx-grid-num" />
          <span className="ffx-grid-h">Item — one per printed line</span>
          <span className="ffx-grid-h ffx-grid-date">Date(s)</span>
          <span className="ffx-grid-clear-sp" />
        </div>
        {Array.from({ length: shown }, (_, i) => {
          const en = entries[i] || { text: '', date: '' };
          return (
            <div className="ffx-grid-row" key={i}>
              <span className="ffx-grid-num">{i + 1}</span>
              <input className="ffx-in" name={`ffx_${f.id}_t${i}`} aria-label={`${f.label} — item ${i + 1}`} value={en.text} disabled={busy}
                onChange={(e) => update(i, 'text', e.target.value)} onBlur={flushGrid} />
              <input className="ffx-in ffx-grid-date" name={`ffx_${f.id}_d${i}`} aria-label={`${f.label} — date ${i + 1}`} placeholder="e.g. 01/2026" value={en.date} disabled={busy}
                onChange={(e) => update(i, 'date', e.target.value)} onBlur={flushGrid} />
              {(en.text || en.date) ? <button type="button" className="ffx-grid-clear" title="Clear this row" disabled={busy} onClick={() => clearRow(i)}>×</button> : <span className="ffx-grid-clear-sp" />}
            </div>
          );
        })}
        <div className="ffx-src">Each row prints on its own line of the form ({maxRows} available{shown < maxRows ? ' — more appear as you fill' : ''}).</div>
      </div>;
    }
    const tall = f.answerBox && (f.answerBox.h || 0) > 0.04;
    const El = tall ? 'textarea' : 'input';
    const uncertain = f.uncertain || (answers[f.id] || {}).uncertain;
    return <El className={'ffx-in' + tcls} name={'ffx_' + f.id} type={tall ? undefined : (f.fieldType === 'numeric' ? 'number' : 'text')}
      placeholder={f.fieldType === 'date' ? 'YYYY-MM-DD' : (f.fieldType === 'icd10' ? 'ICD-10 code' : '')}
      // An UNCERTAIN field is confirmed only by the explicit "Keep" button (awaited) or by editing it —
      // never by a passive blur, whose un-awaited commit could lose to a fast Review click.
      value={v} disabled={busy} onChange={(e) => set(e.target.value)} onBlur={() => { if (hasValue(f.id) && !uncertain) commit(f.id, v); }} />;
  }

  function fieldRow(f) {
    const a = answers[f.id] || {};
    const tier = tierOf(a);
    const big = (f.fieldType === 'radio' || f.fieldType === 'choice' || f.fieldType === 'boolean_ynd') || (f.answerBox && (f.answerBox.h || 0) > 0.04);
    const cov = a.coverage && COV[a.coverage.status];
    // Decision-support affordance. Phase 2: the dx-style fields that always carry it
    // PLUS any not-yet-confirmed field — so the AI never-blank floor (and any provider
    // contextBlocks) is reachable on radio/checkbox/date/numeric/free-text fields too,
    // not just text/icd10/choice. Stays LAZY: the fetch only fires on expand, so a long
    // form doesn't storm the field-options endpoint. Confirmed/trusted fields stay quiet.
    const hasDS = ['text', 'icd10', 'choice'].includes(f.fieldType) || !a.confirmed;
    const ds = dsCache[f.id];
    // An option-bound write-in whose parent question will hold a DIFFERENT option is INACTIVE:
    // input disabled + dimmed, and the server won't print its value (conditionalSuppressed). Mirrors
    // the paper form, where "Please specify: ___" only means something with its own box ticked.
    const inactive = writeInInactive(f, fieldsById, confirmingValue);
    const hasVal = hasValue(f.id);
    // data-fid/data-need power the footer "N to check" jump (query in DOM order —
    // the rendered, sectionized order, which the fields array doesn't mirror).
    const needsReview = !inactive && (tier === 'md' || tier === 'lo');
    return (
      <div className={'ffx-row' + (big ? ' full' : '')} key={f.id} data-fid={f.id} data-need={needsReview ? '1' : undefined} style={inactive ? { opacity: 0.55 } : undefined}>
        <div className="ffx-rl"><span className="dot" style={{ background: DOT[tier] }} /><span className="lab">{f.label}{f.unit ? ` (${f.unit})` : ''}</span>
          {(f.uncertain || a.uncertain) && <span title="Detected inconsistently across reads — this may not be a real field. Confirm it to fill, or leave it blank to skip (it won't block sending)." style={{ marginLeft: 6, fontSize: '0.72em', fontWeight: 700, color: 'var(--lo, #b26a00)', border: '1px solid var(--lo, #b26a00)', borderRadius: 4, padding: '0 4px', cursor: 'help', whiteSpace: 'nowrap' }}>⚠ verify real</span>}</div>
        <div className="ffx-rc">
          {inactive ? (
            <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minInlineSize: 0 }}>{control(f)}</fieldset>
          ) : control(f)}
          {inactive && <div className="ffx-src" style={{ display: 'block', marginTop: 4 }}>
            {hasVal ? 'Won’t be printed — a different option is selected above. Select this option to include it.'
              : 'Applies only to the option above — select it to fill this in.'}
          </div>}
          {fieldErr[f.id] && <div className="ffx-err">{fieldErr[f.id]}</div>}
          <div className="ffx-meta">
            {/* No Keep on an INACTIVE row: confirming a value the row says won't print is a contradiction,
                and if the parent is still unconfirmed the server would fail open and PRINT it. */}
            {(f.uncertain || a.uncertain) && !a.confirmed && !inactive && hasVal &&
              <button type="button" className="ffx-chip" disabled={busy} title="Confirm this is a real field — it will be filled and sent." onClick={async () => { await commit(f.id, values[f.id] != null ? values[f.id] : ''); }}>Keep — it's a real field ✓</button>}
            {cov && <span className={'ffx-cov ' + cov.cls}>{cov.text(a.coverage)}</span>}
            {a.rationale ? <span className="ffx-src">{a.rationale}</span> : (a.source && a.source !== 'none' && a.source !== 'manual' ? <span className="ffx-src">From {a.source === 'identity' ? 'chart/practice record' : a.source}</span> : null)}
            {hasDS && <button type="button" className="ffx-chip" onClick={() => { setOpenDS((m) => ({ ...m, [f.id]: !m[f.id] })); if (!openDS[f.id]) loadDS(f.id); }}>{openDS[f.id] ? 'Hide suggestions ▴' : 'Suggestions ▾'}</button>}
          </div>
          {openDS[f.id] && (
            <div className="ffx-ds">
              {/* The shared decision-support rail (same component order-chat uses): the
                  full covered-dx group, three trust tiers, contextBlocks, demographic
                  "likely — confirm" caveat, citations, and a working ICD search. This
                  replaced an inline onChart-only chip list that dropped every other tier
                  and discarded typed-search hits. Read-and-choose: onPick fills, the
                  clinician still confirms via the field's own control. */}
              <FieldContextRail
                field={f}
                answer={a}
                context={ds || null}
                loading={!ds}
                mode="rail"
                onPick={(value) => { setValues((m) => ({ ...m, [f.id]: String(value) })); commit(f.id, value); }}
                onSearch={(q) => {
                  // The rail fires onSearch('') once on mount; loadDS already fetched the
                  // base options, so ignore the empty query and only re-fetch typed ones.
                  const t = String(q || '').trim();
                  if (!t) return;
                  (async () => {
                    try { const c = await fetchFieldOptions(groupId, f.id, t); setDsCache((m) => ({ ...m, [f.id]: c || { options: [] } })); }
                    catch (_) {}
                  })();
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Stamp queue phase: one card per proposed mark, one tap per decision ──
  function renderQueue() {
    const isInactive = (f) => writeInInactive(f, fieldsById, confirmingValue);
    const items = buildQueue(data, { stampDate, isInactive });
    const pending = items.filter((it) => it.bucket !== 'done');
    const ready = pending.filter((it) => it.bucket === 'ready');
    const imgIds = (data && data.imageIds) || [];

    const ghostFor = (it) => (imgH) => {
      const b = it.box;
      if (!b) return null;
      const fs = Math.max(10, (b.h || 0.018) * imgH * 0.72);
      if (it.mark === 'tick') {
        return <div style={{ position: 'absolute', left: (b.x * 100) + '%', top: b.y * imgH, width: Math.max((b.w || 0.014) * 100, 1) + '%', height: Math.max((b.h || 0.012) * imgH, 10), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1d4ed8', fontWeight: 700, fontSize: fs, outline: '1.5px dashed rgba(29,78,216,0.65)', outlineOffset: 1 }}>X</div>;
      }
      if (it.mark === 'sig') {
        return <div style={{ position: 'absolute', left: (b.x * 100) + '%', top: b.y * imgH, width: (b.w * 100) + '%', height: Math.max((b.h || 0.03) * imgH, 16), display: 'flex', alignItems: 'flex-end', color: '#1d4ed8', fontStyle: 'italic', fontSize: Math.max(11, fs * 0.9), borderBottom: '1.5px dashed rgba(29,78,216,0.65)' }}>✍ your on-file signature</div>;
      }
      return <div style={{ position: 'absolute', left: (b.x * 100) + '%', top: b.y * imgH, maxWidth: Math.max((b.w || 0.2) * 100, 8) + '%', height: Math.max((b.h || 0.018) * imgH, 12), display: 'flex', alignItems: 'flex-end', color: '#1d4ed8', fontSize: fs, whiteSpace: 'nowrap', overflow: 'hidden', borderBottom: '1.5px dashed rgba(29,78,216,0.65)' }}>{it.value}</div>;
    };

    const card = (it) => {
      const imgId = imgIds[it.page - 1];
      const b = it.box;
      const y0 = b ? Math.max(0, b.y - 0.035) : 0;
      const y1 = b ? Math.min(1, b.y + (b.h || 0.02) + 0.035) : 0.1;
      const demoted = it.bucket === 'eye';
      return (
        <div key={it.id} style={{ border: '1px solid var(--line, #e5e0d8)', borderRadius: 8, padding: 10, marginBottom: 10, background: demoted ? 'var(--paper, #fffdf8)' : '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <b style={{ minWidth: 0 }}>{it.label}</b>
            {demoted && <span className="ffx-pos-chip">{it.uncertain ? 'may not be a real field' : 'position estimated — check the crop'}</span>}
            <span className="ffx-src" style={{ marginLeft: 'auto' }}>page {it.page}</span>
          </div>
          {imgId != null && b && <PageCrop src={imageProxyUrl(groupId, imgId)} y0={y0} y1={y1} overlays={ghostFor(it)} />}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {it.bucket === 'choice'
              ? <>{it.options.map((o, i) => (
                  <button key={o.code != null ? o.code : i} type="button" className="ffx-chip" disabled={busy}
                    title={`Stamp “${o.label || o.code}”`}
                    onClick={() => decide([{ id: it.id, action: 'stamp', value: String(o.code) }])}>{o.label || String(o.code)}</button>
                ))}</>
              : it.bucket === 'blank'
                ? <span className="ffx-src">No suggested value — Skip leaves it blank, or type it in the form editor.</span>
                : <button type="button" className="ffx-btn-brand" disabled={busy}
                    onClick={() => decide([{ id: it.id, action: 'stamp' }])}>Stamp ✓</button>}
            <button type="button" className="ffx-chip" disabled={busy}
              title="Leave this blank on the sent form (recorded as your decision)"
              onClick={() => decide([{ id: it.id, action: 'skip' }])}>Skip</button>
            {it.kind === 'field' && <button type="button" className="ffx-chip" disabled={busy}
              onClick={() => { setPhase('fill'); setTimeout(() => { const row = document.querySelector(`.ffx .ffx-row[data-fid="${it.id}"]`); if (row) { row.scrollIntoView({ behavior: 'smooth', block: 'center' }); const inp = row.querySelector('input, textarea, select'); if (inp && inp.focus) inp.focus({ preventScroll: true }); } }, 60); }}>Edit…</button>}
            {b && <button type="button" className="ffx-chip" disabled={busy} onClick={() => startReposition(it.id, it.label)}>Move…</button>}
          </div>
        </div>
      );
    };

    return (
      <div className="ffx-body"><div className="ffx-sheet">
        {ready.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: 10, border: '1px solid var(--line, #e5e0d8)', borderRadius: 8, background: '#fff' }}>
            <span><b className="tnum">{ready.length}</b> proposal{ready.length !== 1 ? 's are' : ' is'} ready — value and position both verified.</span>
            <div className="ffx-spacer" />
            <button type="button" className="ffx-btn-brand" disabled={busy}
              onClick={() => decide(ready.map((it) => ({ id: it.id, action: 'stamp' })))}>{busy ? 'Stamping…' : `Stamp all ${ready.length} ✓`}</button>
          </div>
        )}
        {pending.length === 0 && <p style={{ color: 'var(--pink-muted)' }}>Every mark is decided — review the final render and send.</p>}
        {pending.filter((it) => it.bucket === 'eye').map(card)}
        {pending.filter((it) => it.bucket === 'ready').map(card)}
        {pending.filter((it) => it.bucket === 'choice' || it.bucket === 'blank').map(card)}
        {error && <div className="ffx-warn">{error}</div>}
      </div></div>
    );
  }

  function renderFill() {
    const groups = sectionize(fields);
    return (
      <div className="ffx-body"><div className="ffx-sheet">
        {groups.map((g, gi) => (
          <div className="ffx-section" key={gi}>
            {g.title && <div className="ffx-section-head"><div className="ffx-section-title">{g.title}</div></div>}
            <div className="ffx-rows">{g.fields.map(fieldRow)}</div>
          </div>
        ))}
        {error && <div className="ffx-warn">{error}</div>}
      </div></div>
    );
  }

  // Touch-up: every drawn mark overlaid on the page images as a tappable object. Tap a mark ("move
  // this"), tap the spot it belongs — the backend re-places it and the preview re-renders. This is
  // the manual fallback for anything automation got wrong; flagged marks carry a ring so attention
  // lands where it's needed, but ANY mark can be corrected, gated or not.
  function renderTouchUp() {
    const marks = (preview && preview.marks) || [];
    const flagged = new Set([
      ...pendingPositions(data).map((f) => f.id),
      ...(((preview && preview.qa && preview.qa.findings) || []).map((f) => f.id)),
    ]);
    const imgIds = (data && data.imageIds) || [];
    const placing = !!fixSel;
    return (
      <div className={'ffx-touch' + (placing ? ' placing' : '')}>
        <div className="ffx-touch-hint">{placing
          ? <><b>{fixSel.kind === 'tick'
              ? <>Now tap the exact BOX where “{fixSel.label}” goes.</>
              : <>Now tap the spot on the line where “{fixSel.label}” should START — the writing begins at your tap.</>}</b> <button type="button" className="ffx-chip" onClick={() => setFixSel(null)}>Cancel</button></>
          : 'Tap any mark to move it. Marks with a ring were flagged for a second look.'}</div>
        {imgIds.map((imgId, i) => {
          const page = i + 1;
          const pageMarks = marks.filter((m) => m.page === page);
          if (!pageMarks.length && !placing) return null; // only show pages that carry ink (all pages while placing)
          return (
            <div key={page} className="ffx-touch-page" data-page={page}>
              <div className="ffx-touch-imgwrap" onClick={(e) => {
                if (!placing || busy) return;
                const r = e.currentTarget.getBoundingClientRect();
                if (!r.width || !r.height) return;
                fixHere(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)));
              }}>
                <img src={imageProxyUrl(groupId, imgId)} alt={`Page ${page}`} draggable={false} />
                {pageMarks.map((m) => (
                  <button key={m.id + ':' + m.box.x + ':' + m.box.y} type="button"
                    className={'ffx-mark' + (m.fieldType === 'checkbox' || m.fieldType === 'radio' ? ' tick' : '') + (flagged.has(m.id) ? ' flag' : '') + (fixSel && fixSel.id === m.id ? ' sel' : '')}
                    style={{ left: (m.box.x * 100) + '%', top: (m.box.y * 100) + '%', width: Math.max(m.box.w * 100, 1.2) + '%', height: Math.max(m.box.h * 100, 0.8) + '%' }}
                    title={`${m.label} — tap to move`} disabled={busy}
                    onClick={(e) => { e.stopPropagation(); setFixSel(fixSel && fixSel.id === m.id ? null : { id: m.id, label: m.label, page: m.page, kind: (m.fieldType === 'checkbox' || m.fieldType === 'radio') ? 'tick' : 'text' }); }} />
                ))}
              </div>
              <div className="ffx-src" style={{ textAlign: 'center' }}>Page {page}</div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderReview() {
    const blankLabels = fields.filter((f) => !(answers[f.id] && answers[f.id].confirmed)).map((f) => f.label);
    const destFax = preview && preview.returnFax;
    const showSignerWarn = preview && !preview.signerOk && applySig;
    const pendingF = pendingPositions(data);
    const pendingS = pendingSignaturePlacements(data, { applySig, stampDate, signerOk: !!(preview && preview.signerOk) });
    return (
      <div className="ffx-body"><div className="ffx-review">
        <p className="ffx-review-intro">Review on the original — your inputs applied to the received fax. This is exactly what will be sent. On <b>Confirm &amp; send</b> it's signed by the chart PCP and faxed back.</p>
        {!previewLoading && preview && (preview.marks || []).length > 0 && (
          <div className="ffx-touch-toggle" role="tablist">
            <button type="button" className={'ffx-chip' + (!touchUp ? ' on' : '')} onClick={() => { setTouchUp(false); setFixSel(null); }}>Preview</button>
            <button type="button" className={'ffx-chip' + (touchUp ? ' on' : '')} onClick={() => setTouchUp(true)}>Touch up positions</button>
          </div>
        )}
        {previewLoading && (
          <div className="ffx-preview-skel" role="status" aria-live="polite">
            <div className="ffx-preview-skel-page" />
            <div className="ffx-src">Rendering your inputs onto the original fax… this takes a few seconds.</div>
          </div>
        )}
        {!previewLoading && previewError && <div className="ffx-warn">Could not render a preview: {previewError}. You can still go Back or close.</div>}
        {!previewLoading && touchUp && preview && renderTouchUp()}
        {!previewLoading && !touchUp && preview && preview.url && <>
          <iframe className="ffx-preview-frame" title="Filled form applied to the original" src={preview.url} />
          <div className="ffx-src" style={{ marginTop: 6 }}>Preview not showing? <a href={preview.url} target="_blank" rel="noopener noreferrer">Open it in a new tab ↗</a></div>
        </>}
        {!previewLoading && !touchUp && preview && !preview.url && !previewError && <div className="ffx-warn">Couldn't render an inline preview — review the values; nothing sends until Confirm &amp; send.</div>}
        {!previewLoading && preview && <div style={{ marginTop: 12 }}><span className="ffx-pill-info">{destFax ? <>Will fax to: <b style={{ marginLeft: 4 }}>{destFax}</b></> : 'Chart copy only — no return fax'}</span></div>}
        {showSignerWarn && <div className="ffx-warn">No signature on file for the chart PCP — uncheck "Apply my on-file signature" or add one in Settings.</div>}
        {preview && (preview.healed || []).length > 0 && (
          <div className="ffx-src" style={{ display: 'block', marginTop: 8 }}>
            ✓ Auto-adjusted after verification: {preview.healed.map((h) => h.label).join(', ')}.
          </div>
        )}
        {(() => {
          // ONE position panel, one flow. Every item's primary action is "Fix on page" (touch-up
          // placing, scrolled to its page); a drawn-but-flagged mark can instead be waved through
          // with "Looks right". Replaces three differently-worded warning boxes and three
          // different correction verbs that all meant "check where this mark goes".
          const items = [
            ...((preview && preview.unrendered) || []).map((u) => ({ id: u.id, label: u.label, page: u.page, kind: 'unplaced', mark: (u.fieldType === 'checkbox' || u.fieldType === 'radio') ? 'tick' : 'text' })),
            ...pendingF.map((f) => ({ id: f.id, label: f.label, page: Number(f.page) || 1, kind: 'confirm', mark: (f.fieldType === 'checkbox' || f.fieldType === 'radio') ? 'tick' : 'text' })),
            ...pendingS.map((s) => ({ id: s.id, label: s.label, page: Number((/(\d+)$/.exec(s.id) || [])[1]) || 1, kind: 'confirm', mark: 'text' })),
          ];
          if (!items.length) return null;
          const nBlock = items.filter((i) => i.kind === 'unplaced').length;
          return (
            <div className="ffx-pos">
              <div className="ffx-pos-head">
                <b>{items.length === 1 ? 'One mark needs' : `${items.length} marks need`} a quick position check</b>
                <span className="ffx-src">{nBlock ? 'Items marked “won’t print” block sending until placed.' : 'Tap Fix on page, then tap the exact spot on the form.'}</span>
              </div>
              {items.map((it) => (
                <div key={it.id} className="ffx-pos-row">
                  <span className="ffx-pos-label">{it.label}</span>
                  <span className={'ffx-pos-chip' + (it.kind === 'unplaced' ? ' block' : '')}>{it.kind === 'unplaced' ? 'won’t print' : 'double-check'}</span>
                  <span className="ffx-pos-actions">
                    {it.kind === 'confirm' && <button className="ffx-chip" disabled={busy} onClick={() => fixPlacement(it.id, { type: 'confirm' })}>Looks right ✓</button>}
                    <button className="ffx-chip" disabled={busy} onClick={() => { setTouchUp(true); setFixSel({ id: it.id, label: it.label, page: it.page, kind: it.mark }); }}>Fix on page</button>
                  </span>
                </div>
              ))}
            </div>
          );
        })()}
        {blankLabels.length > 0 && <div className="ffx-warn">These {blankLabels.length} field(s) will be blank on the faxed form: {blankLabels.join(', ')}.</div>}
        <div className="ffx-review-opts">
          <label><input type="checkbox" checked={applySig} disabled={busy} onChange={(e) => setApplySig(e.target.checked)} /> Apply my on-file signature</label>
          <label><input type="checkbox" checked={stampDate} disabled={busy} onChange={(e) => setStampDate(e.target.checked)} /> Stamp today's date</label>
        </div>
        {error && <div className="ffx-err">{error}</div>}
      </div></div>
    );
  }

  // Click-to-place overlay: show the page raster, let the reviewer click where the
  // mark should go, send it as an absolute placement. Replaces the ↑/↓ nudge.
  function renderReposition() {
    if (!reposition) return null;
    const imgId = ((data && data.imageIds) || [])[reposition.page - 1];
    const src = imgId != null ? imageProxyUrl(groupId, imgId) : null;
    const cur = reposition.box || {};
    const onPick = (e) => {
      const r = e.currentTarget.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const x = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
      const y = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
      setPlacePt({ x, y });
    };
    const close = () => { setReposition(null); setPlacePt(null); };
    return (
      <div className="ffx-place" role="dialog" aria-modal="true" aria-label="Reposition mark" onClick={(e) => { if (e.target === e.currentTarget && !busy) close(); }}>
        <div className="ffx-place-card">
          <div className="ffx-place-head">
            <div className="ffx-place-title"><b>Reposition:</b> {reposition.label}</div>
            <div className="ffx-src">Click on the form where this mark should sit — page {reposition.page}.</div>
          </div>
          <div className="ffx-place-stage">
            {src
              ? <div className="ffx-place-imgwrap" onClick={onPick}>
                  <img src={src} alt={`Page ${reposition.page}`} draggable="false" />
                  <span className="ffx-place-cur" title="Current position"
                    style={{ left: cur.x * 100 + '%', top: cur.y * 100 + '%', width: Math.max(cur.w || 0, 0.012) * 100 + '%', height: Math.max(cur.h || 0, 0.012) * 100 + '%' }} />
                  {placePt && <span className="ffx-place-pin" style={{ left: placePt.x * 100 + '%', top: placePt.y * 100 + '%' }} />}
                </div>
              : <div className="ffx-warn">Page image unavailable — use “Confirm position” or “Move to row” instead.</div>}
          </div>
          <div className="ffx-place-foot">
            <span className="ffx-src">{placePt ? 'New spot chosen — Place to apply.' : <><span className="ffx-place-key cur" /> current &nbsp; · &nbsp; click to choose a new spot</>}</span>
            <div className="ffx-spacer" />
            <button className="ffx-btn-ghost" disabled={busy} onClick={close}>Cancel</button>
            <button className="ffx-btn-brand" disabled={busy || !placePt} onClick={placeHere}>{busy ? 'Placing…' : 'Place here'}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ffx" role="dialog" aria-modal="true" aria-label={headTitle}>
      <div className="ffx-top">
        <div style={{ minWidth: 0 }}>
          <div className="ffx-eyebrow">Form fill · confidence-scored{phase === 'fill' && fields.length > 0 ? <> · <b className="tnum">{prefilledN}/{fields.length}</b> prefilled</> : null}</div>
          <div className="ffx-title">{headTitle}{!noFields && phase !== 'loading' && phase !== 'error' ? <span className="muted"> · reconstructed form</span> : null}</div>
        </div>
        <div className="ffx-spacer" />
        {!noFields && phase !== 'loading' && phase !== 'error' && (
          <div className="ffx-stepper">
            <span className={'st' + (phase === 'queue' || phase === 'fill' ? ' on' : ' done')}><span className="num">1</span> {phase === 'fill' ? 'Fill' : 'Stamp'}</span>
            <span className="arrow">→</span>
            <span className={'st' + (phase === 'review' || phase === 'done' ? ' on' : '')}><span className="num">2</span> Review &amp; send</span>
          </div>
        )}
        <button className="ffx-icon" onClick={onClose} disabled={busy} aria-label="Close">✕</button>
      </div>

      {phase === 'loading' && <div className="ffx-body"><div className="ffx-sheet"><p style={{ color: 'var(--pink-muted)' }}>Loading…</p></div></div>}
      {phase === 'error' && <div className="ffx-body"><div className="ffx-sheet"><p className="ffx-err">{error || 'Could not load this form.'}</p></div></div>}
      {noFields && phase !== 'error' && phase !== 'loading' && <div className="ffx-body"><div className="ffx-sheet"><p style={{ color: 'var(--pink-muted)' }}>This form has no fillable fields. Close and handle it in Elation.</p></div></div>}
      {phase === 'queue' && !noFields && renderQueue()}
      {phase === 'fill' && !noFields && renderFill()}
      {phase === 'review' && !noFields && renderReview()}
      {phase === 'done' && <div className="ffx-body"><div className="ffx-done"><div className="check">✓</div><h3 style={{ margin: '0 0 6px' }}>Filled, signed, and faxed back</h3><p style={{ color: 'var(--pink-muted)' }}>The completed form was signed by the chart PCP and faxed to the sender.</p></div></div>}

      <div className="ffx-foot">
        {(phase === 'loading' || phase === 'error' || noFields) && <><div className="ffx-spacer" /><button className="ffx-btn-ghost" onClick={onClose}>Close</button></>}
        {phase === 'queue' && !noFields && <>
          <button className="ffx-btn-ghost" disabled={busy} onClick={() => setPhase('fill')}>Edit as form</button>
          <div className="ffx-spacer" />
          {(() => {
            const isInactive = (f) => writeInInactive(f, fieldsById, confirmingValue);
            const n = buildQueue(data, { stampDate, isInactive }).filter((it) => it.bucket !== 'done').length;
            return n > 0
              ? <span className="ffx-count tnum"><b>{n}</b> to decide</span>
              : <span className="ffx-count tnum">all decided ✓</span>;
          })()}
          <button className="ffx-btn-brand" disabled={busy} onClick={() => { setError(null); setPhase('review'); }}>Review on original →</button>
        </>}
        {phase === 'fill' && !noFields && <>
          <button className="ffx-btn-ghost" disabled={busy} onClick={() => { setError(null); setPhase('queue'); }}>← Stamp queue</button>
          <span className="ffx-leg"><span className="sw" style={{ background: 'var(--hi)' }} />trusted</span>
          <span className="ffx-leg"><span className="sw" style={{ background: 'var(--md)' }} />review</span>
          <span className="ffx-leg"><span className="sw" style={{ background: 'var(--lo)' }} />low — check</span>
          <span className="ffx-leg"><span className="sw" style={{ border: '1px dashed var(--pline-strong)', background: '#fff' }} />blank</span>
          <div className="ffx-spacer" />
          {reviewN > 0
            ? <button type="button" className="ffx-count ffx-count-jump tnum" title="Jump to the next field to check" onClick={jumpToNextReview}><b>{reviewN}</b> field{reviewN !== 1 ? 's' : ''} to review ↓</button>
            : <span className="ffx-count tnum">all fields reviewed ✓</span>}
          <button className="ffx-btn-brand" disabled={busy} onClick={toReview}>{busy ? 'Saving…' : 'Review on original →'}</button>
        </>}
        {phase === 'review' && !noFields && <>
          <button className="ffx-btn-ghost" onClick={() => { setError(null); setPhase('queue'); }} disabled={busy}>← Back</button>
          <div className="ffx-spacer" />
          <button className={'ffx-btn-brand' + (positionsPending > 0 ? '' : ' ready')} onClick={complete} disabled={busy || positionsPending > 0} title={positionsPending > 0 ? 'Confirm the flagged positions first' : undefined}>{busy ? 'Sending…' : 'Confirm & send →'}</button>
        </>}
        {phase === 'done' && <><div className="ffx-spacer" /><button className="ffx-btn-brand" onClick={onClose}>Close</button></>}
      </div>
      {renderReposition()}
    </div>
  );
}
